import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import { useQueryParamTerm, useQueryParamsEffect } from 'pricing-pages-ui/utils/urls/appQueryParamUtils';
import { useEffect } from 'react';
import { NavMarker } from 'react-rhumb';
import { useHistory } from 'react-router-dom';
import { MARKETING_PLUS_PROFESSIONAL_BUNDLE } from 'self-service-api/constants/ApiNames';
import { MARKETING_PLUS_SUITE } from 'self-service-api/constants/BundleTypes';
import * as ProductLines from 'self-service-api/constants/ProductLines';
import { buildFilteredHighlightedProducts } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import { OWNERSHIP_CHECK_FAILED } from '../../constants/ErrorCauses';
import { getMerchCardsToShowForBundle } from '../../constants/merchCards/MerchCardsToShow';
import { NavItemEnum } from '../../constants/routing/NavItems';
import Screens from '../../constants/routing/Screens';
import { ENTERPRISE } from '../../constants/routing/Tabs';
import { useAnchorLinkScroll } from '../../hooks/htmlElementHooks';
import { useBusinessObjectives } from '../../hooks/useBusinessObjectives';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useFeatureStore } from '../../hooks/useFeatureStore';
import { useTrackRetail } from '../../hooks/useTrackRetail';
import { useAppSelector } from '../../reducers';
import { isUngatedForMarketingPlusSelector } from '../../selectors/GatingSelectors';
import { hasOnlyOwnershipCheckFailedSelector, hasPricingAndPurchasingFailedSelector, hasPricingAndPurchasingSucceededSelector, hasProductsSucceededSelector } from '../../selectors/LoadingStateSelectors';
import { bundleProductsForBundleTypeSelector } from '../../selectors/ProductsSelectors';
import { getPageViewEvent } from '../../utils/event-tracking/interactions';
import { trackEvent } from '../../utils/event-tracking/track-event';
import { useSSM0004ExperimentEffect } from '../../utils/experiments/ssm0004';
import { getIsAcceptanceTest } from '../../utils/testUtils';
import BannerSection from '../banners/BannerSection';
import IntegrationsSection from '../common/Integrations';
import PhoneSupport from '../common/PhoneSupport';
import PhoneSupportFooter from '../common/PhoneSupportFooter';
import ProductInfoContainer from '../common/ProductInfoContainer';
import ValuePropsSection from '../common/ValuePropsSection';
import CodeSplitEnvWrapper from '../common/env/CodeSplitEnvWrapper';
import FailedLoadBanner from '../common/loading/FailedLoadBanner';
import FallbackErrorBanner from '../common/loading/FallbackErrorBanner';
import SkeletonLoadWrapper from '../common/loading/SkeletonLoadWrapper';
import CompareStatusFooter from '../compare/CompareStatusFooter';
import ErrorPage from '../error-page/ErrorPage';
import GenericBundleFeatureTable from '../feature-table/suite-feature-table/GenericBundleFeatureTable';
import HeaderLayout from '../layout/HeaderLayout';
import MarketingPlusTiles from '../marketing-plus/MarketingPlusTiles';
import ProductPageSubtitle from '../product-pages/ProductPageSubtitle';
import ProductPageTitle, { ProductPageHeader } from '../product-pages/ProductPageTitle';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MarketingPlusPage = () => {
  useAnchorLinkScroll();
  useTrackRetail();
  useDocumentTitle('marketing-plus');
  const hasPricingAndPurchasingSucceeded = useAppSelector(hasPricingAndPurchasingSucceededSelector);
  const hasPricingAndPurchasingFailed = useAppSelector(hasPricingAndPurchasingFailedSelector);
  const hasOnlyOwnershipCheckFailed = useAppSelector(hasOnlyOwnershipCheckFailedSelector);
  const screen = Screens.MARKETING_PLUS;
  const navItem = NavItemEnum.MARKETING_PLUS;
  const hasProductsSucceeded = useAppSelector(hasProductsSucceededSelector);
  const isUngatedForMarketingPlus = useAppSelector(isUngatedForMarketingPlusSelector);
  const isAcceptanceTest = getIsAcceptanceTest();
  const history = useHistory();
  useEffect(() => {
    if (!hasProductsSucceeded) return;
    if (!isUngatedForMarketingPlus && !isAcceptanceTest) {
      history.replace('/suite');
    }
    trackEvent(getPageViewEvent({
      screen
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasProductsSucceeded]);
  const highlightedProducts = useAppSelector(state => {
    if (hasPricingAndPurchasingFailed) return [];
    return bundleProductsForBundleTypeSelector(state, {
      bundleType: MARKETING_PLUS_SUITE
    }).map(({
      apiName
    }) => apiName);
  });
  useBusinessObjectives({
    highlightedProducts
  });
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  const productLevels = getMerchCardsToShowForBundle({
    bundleType: MARKETING_PLUS_SUITE
  });
  useFeatureStore({
    highlightedProducts: buildFilteredHighlightedProducts({
      productLine: ProductLines.MARKETING,
      productLevels
    }),
    productLine: ProductLines.MARKETING
  });
  useFeatureStore({
    highlightedProducts: buildFilteredHighlightedProducts({
      productLine: ProductLines.CMS,
      productLevels
    }),
    productLine: ProductLines.CMS
  });
  const {
    isSSM0004Loading
  } = useSSM0004ExperimentEffect();
  if (hasPricingAndPurchasingFailed) {
    if (hasOnlyOwnershipCheckFailed) {
      return /*#__PURE__*/_jsx(ErrorPage, {
        errorCause: OWNERSHIP_CHECK_FAILED
      });
    }
  }
  if (!hasProductsSucceeded || isSSM0004Loading) {
    return /*#__PURE__*/_jsx(UILoadingSpinner, {
      minHeight: 200,
      size: "md",
      grow: true
    });
  }
  return /*#__PURE__*/_jsxs("div", {
    "data-selenium-id": "page-success",
    children: [hasPricingAndPurchasingSucceeded && /*#__PURE__*/_jsx(NavMarker, {
      name: "MARKETING_PLUS_PAGE"
    }), /*#__PURE__*/_jsx(HeaderLayout, {
      selectedNavItem: navItem,
      children: /*#__PURE__*/_jsxs(ProductInfoContainer, {
        children: [/*#__PURE__*/_jsx(BannerSection, {
          screen: screen
        }), /*#__PURE__*/_jsx(FailedLoadBanner, {
          hasFailed: hasPricingAndPurchasingFailed
        }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsxs(ProductPageHeader, {
          children: [/*#__PURE__*/_jsx(ProductPageTitle, {
            nonProductIdentifier: navItem
          }), /*#__PURE__*/_jsx(ProductPageSubtitle, {
            nonProductIdentifier: navItem,
            hasCalculatorLink: false
          })]
        }), /*#__PURE__*/_jsx(MarketingPlusTiles, {
          screen: screen
        }), /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
          isLoading: !hasPricingAndPurchasingSucceeded,
          heights: [40],
          children: /*#__PURE__*/_jsx(GenericBundleFeatureTable, {
            apiNames: highlightedProducts,
            screen: screen
          }, screen)
        }), /*#__PURE__*/_jsx(PhoneSupport, {
          phoneSourceId: "bundle"
          // phoneSourceId="marketingplus-middle"
          ,
          screen: screen
        }), /*#__PURE__*/_jsx(IntegrationsSection, {}), /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
          publicComponentLoader: () => import( /* webpackChunkName: "public-chunk" */`pricing-pages-ui/components/feature-modal/FeatureCardWrapper`),
          publicComponentProps: {
            apiName: MARKETING_PLUS_PROFESSIONAL_BUNDLE,
            navItem: NavItemEnum.MARKETING_PLUS
          }
        }), /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
          publicComponentLoader: () => import( /* webpackChunkName: "public-chunk" */`pricing-pages-ui/components/company-proof/CompanyProof`),
          publicComponentProps: {
            tab: ENTERPRISE,
            screen
          }
        }), /*#__PURE__*/_jsx(ValuePropsSection, {}), /*#__PURE__*/_jsx(PhoneSupportFooter, {
          phoneSourceId: "bundle"
          // phoneSourceId="marketingplus-footer"
          ,
          screen: screen
        })]
      })
    }), /*#__PURE__*/_jsx(CompareStatusFooter, {})]
  });
};
export default MarketingPlusPage;